import React, { useMemo } from 'react';
import { useForm } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
} from '../../components/ui/combobox';
import { useTrans } from '../../hooks/useTrans';

function SettingField({ label, items, value, onChange, placeholder }) {
    const translate = useTrans();
    const allItems = useMemo(() => [
        { value: '', label: placeholder },
        ...items,
    ], [items, placeholder]);

    return (
        <div className="flex items-center gap-4">
            <label className="w-48 text-sm font-bold text-[#003545] shrink-0">
                {label}
            </label>
            <div className="w-full max-w-md">
                <Combobox
                    items={allItems}
                    value={allItems.find((o) => String(o.value) === String(value)) ?? allItems[0]}
                    onValueChange={(item) => onChange(item?.value ?? '')}
                    itemToStringValue={(item) => item.label}
                >
                    <ComboboxTrigger render={
                        <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none">
                            <ComboboxValue placeholder={placeholder} />
                        </Button>
                    } />
                    <ComboboxContent>
                        <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                        <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                        <ComboboxList>
                            {(item) => (
                                <ComboboxItem key={item.value} value={item}>
                                    {item.label}
                                </ComboboxItem>
                            )}
                        </ComboboxList>
                    </ComboboxContent>
                </Combobox>
            </div>
        </div>
    );
}

function DebtorSettings({ debtor, formData, onFieldChange }) {
    const translate = useTrans();

    const pickupOptions = useMemo(() => (debtor.pickup_addresses?.data || []).map((a) => ({
        value: a.id,
        label: `${a.company_name} (${a.street} ${a.house_number}${a.house_number_addition || ''} ${a.place})`,
    })), [debtor.pickup_addresses]);

    const deliveryOptions = useMemo(() => (debtor.delivery_addresses?.data || []).map((a) => ({
        value: a.id,
        label: `${a.company_name} (${a.street} ${a.house_number}${a.house_number_addition || ''} ${a.place})`,
    })), [debtor.delivery_addresses]);

    const referenceOptions = useMemo(() => (debtor.references?.data || []).map((r) => ({
        value: r.id,
        label: `${r.reference} - ${r.description}`,
    })), [debtor.references]);

    const contactOptions = useMemo(() => (debtor.contacts?.data || []).map((c) => ({
        value: c.id,
        label: c.name,
    })), [debtor.contacts]);

    const transportTypeOptions = useMemo(() => (debtor.transport_types?.data || []).map((t) => ({
        value: t.id,
        label: `${t.code} - ${t.description}`,
    })), [debtor.transport_types]);

    return (
        <div className="space-y-6">
            <h3 className="text-2xl font-bold text-[#003545]">
                {translate('Standaard instellingen voor :company_name', { company_name: debtor.debtor.company_name })}
            </h3>

            <SettingField
                label={translate('Laadadres')}
                items={pickupOptions}
                value={formData.pickup_address}
                onChange={(value) => onFieldChange('pickup_address', value)}
                placeholder={translate('Gebruik standaard laadadres')}
            />
            <SettingField
                label={translate('Losadres')}
                items={deliveryOptions}
                value={formData.delivery_address}
                onChange={(value) => onFieldChange('delivery_address', value)}
                placeholder={translate('Gebruik standaard losadres')}
            />
            <SettingField
                label={translate('Referentie')}
                items={referenceOptions}
                value={formData.reference}
                onChange={(value) => onFieldChange('reference', value)}
                placeholder={translate('Gebruik standaard referentie')}
            />
            <SettingField
                label={translate('Contactpersoon')}
                items={contactOptions}
                value={formData.contact}
                onChange={(value) => onFieldChange('contact', value)}
                placeholder={translate('Gebruik standaard contactpersoon')}
            />
            <SettingField
                label={translate('Transporttype')}
                items={transportTypeOptions}
                value={formData.transport_type}
                onChange={(value) => onFieldChange('transport_type', value)}
                placeholder={translate('Gebruik standaard transporttype')}
            />
        </div>
    );
}

export default function SettingsIndex({ debtors }) {
    const translate = useTrans();

    const initialData = useMemo(() => {
        const debtor = {};
        (debtors || []).forEach((d) => {
            debtor[d.debtor.id] = {
                pickup_address: d.defaults?.pickup_address || '',
                delivery_address: d.defaults?.delivery_address || '',
                reference: d.defaults?.reference || '',
                contact: d.defaults?.contact || '',
                transport_type: d.defaults?.transport_type || '',
            };
        });
        return { debtor };
    }, []);

    const { data, setData, post, processing } = useForm(initialData);

    function handleFieldChange(debtorId, field, value) {
        setData('debtor', {
            ...data.debtor,
            [debtorId]: {
                ...data.debtor[debtorId],
                [field]: value,
            },
        });
    }

    function handleSubmit(e) {
        e.preventDefault();
        post('/settings');
    }

    if (!debtors || debtors.length === 0) {
        return (
            <div className="max-w-[1200px] mx-auto px-4">
                <div className="bg-white rounded-[3px] border border-[#bbb]">
                    <div className="p-[30px]">
                        <div className="bg-blue-50 border border-blue-200 rounded p-4">
                            <h5 className="font-bold text-[#003545]">{translate('user.no_debtors.title')}</h5>
                            <p className="text-sm text-[#003545] mt-1">{translate('user.no_debtors.message.0')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px]">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {debtors.map((debtor, index) => (
                            <div key={debtor.debtor.id}>
                                <DebtorSettings
                                    debtor={debtor}
                                    formData={data.debtor[debtor.debtor.id]}
                                    onFieldChange={(field, value) => handleFieldChange(debtor.debtor.id, field, value)}
                                />
                                {index < debtors.length - 1 && <hr className="mt-8 border-[#bbb]" />}
                            </div>
                        ))}

                        <hr className="border-[#bbb]" />

                        <div className="flex justify-end">
                            <Button variant="primary" type="submit" disabled={processing}>
                                {translate('Opslaan')}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
